import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';

import { LoggerService } from '@gde-mit/angular-core';
import { SnackBarService } from '@gde-mit/angular-design-system';

import { ktMediaPathDisplayUrl } from '@features/kt/activity-browser/constants/kt-image-source';
import { ActivityService } from '@features/kt/activity-browser/services/activity.service';
import { ACTIVITY_BROWSER_I18N_PREFIX } from '@features/kt/kt.transloco';

import { httpErrorStatus } from '@shared/utils/http-error-status.util';
import { httpErrorTitle } from '@shared/utils/http-error-title.util';

import { TranslocoService } from '@jsverse/transloco';

import { AppSettingsService } from '../../../../../app-settings.service';
import { ACTIVITY_EDIT_STICKER_ERROR_SNACKBAR_TEST_ID } from '../constants/activity-edit-test-ids';
import type { ActivityEditFlushResult } from '../models/activity-edit-field.model';
import { ActivityEditStore } from './activity-edit.store';

const SAVE_FAILED_MESSAGE_KEY = `${ACTIVITY_BROWSER_I18N_PREFIX}.DETAIL.EDIT_PAGE.STICKER.ERRORS.SAVE_FAILED`;
const GENERATION_FAILED_MESSAGE_KEY = `${ACTIVITY_BROWSER_I18N_PREFIX}.DETAIL.EDIT_PAGE.STICKER.ERRORS.GENERATION_FAILED`;
const UNAVAILABLE_HERE_MESSAGE_KEY = `${ACTIVITY_BROWSER_I18N_PREFIX}.DETAIL.EDIT_PAGE.STICKER.ERRORS.UNAVAILABLE_HERE`;
const IMAGE_PIPELINE_DISABLED_STATUS = 400;
const IMAGE_PIPELINE_DISABLED_TITLE = 'ImageUpload.Disabled';

const REGENERATION_ACTION_LABELS_KEY = `${ACTIVITY_BROWSER_I18N_PREFIX}.DETAIL.EDIT_PAGE.STICKER.REGENERATION_ACTION_LABELS`;
const REGENERATION_ACTION_VARIANTS = ['A', 'B', 'C', 'D'] as const;
const REGENERATION_ACTION_STEPS = ['STEP_1', 'STEP_2', 'STEP_3', 'STEP_4'] as const;

type PrepareStickerGeneration = () => Promise<ActivityEditFlushResult>;

interface StickerGenerationRun {
  readonly activityId: string;
  readonly number: number;
  readonly prepare: PrepareStickerGeneration;
  readonly signal: AbortSignal;
  readonly teacherRequest: string | undefined;
  readonly onRequestSent: (() => void) | undefined;
}

/**
 * Single owner of the sticker shown in the activity editor.
 *
 * Provided on the edit route next to {@link ActivityEditStore}, so it outlives the page: the page must call
 * {@link reset} when it is destroyed. The shown sticker is seeded whenever the edit store lands a draft from the
 * server, follows a sticker change a silent reload brings into the draft, and otherwise changes only through a
 * successful generation; an autosave never touches it.
 */
// eslint-disable-next-line @angular-eslint/use-injectable-provided-in -- route-scoped store, provided via route `providers`
@Injectable()
export class ActivityStickerStore {
  readonly #editStore = inject(ActivityEditStore);
  readonly #activityService = inject(ActivityService);
  readonly #appSettings = inject(AppSettingsService);
  readonly #snackBar = inject(SnackBarService);
  readonly #transloco = inject(TranslocoService);
  readonly #logger = inject(LoggerService);

  readonly #stickerPath = signal<string | null>(null);
  readonly #generating = signal(false);
  readonly actualRegenerationActionIndex = signal<number>(0);
  readonly #regenerationActionLabels = signal<readonly string[]>([]);

  readonly #draftStickerPath = computed(() => this.#editStore.draft()?.stickerImageUrl ?? null);
  /** The sticker's address on the media host, or `null` when there is none to show. */
  readonly stickerUrl = computed(() => {
    const path = this.#stickerPath();

    return path === null ? null : ktMediaPathDisplayUrl(path, this.#appSettings.imageMediaBaseUrl);
  });

  /** The activity's name in the draft, the source of the download file name. */
  readonly activityName = this.#editStore.name;
  /** The progress steps shown while generating: one random variant, chosen anew by every {@link generate}. */
  readonly stickerRegenerationActionLabels = this.#regenerationActionLabels.asReadonly();
  #prepare: PrepareStickerGeneration | null = null;
  #actionTimers: ReturnType<typeof setTimeout>[] = [];
  /** Bumped by every generation and by {@link #invalidateRun}; a call whose number is no longer current is dropped. */
  #runNumber = 0;
  #abortController: AbortController | null = null;
  /**
   * The edit store's load count when this store was created or last reset. The edit store outlives the page, so drafts
   * it landed before that point belong to an earlier visit and must never seed the sticker.
   */
  #seenLoadCount = this.#editStore.draftLoadCount();
  #loadedActivityId: string | null = null;
  /** True from the click until the generation settles, including the pre-generation save. */
  readonly generating = this.#generating.asReadonly();

  constructor() {
    effect(() => {
      const loadCount = this.#editStore.draftLoadCount();
      untracked(() => {
        this.#seedWhenLoadLanded(loadCount);
      });
    });

    effect(() => {
      const path = this.#draftStickerPath();
      untracked(() => {
        this.#followDraftSticker(path);
      });
    });
  }

  /**
   * Registers how pending edits are saved before a generation. Cleared by {@link reset}.
   *
   * @param prepare Saves the pending edits; resolves to what that flush found.
   */
  setPrepare(prepare: PrepareStickerGeneration): void {
    this.#prepare = prepare;
  }

  /**
   * Generates a sticker for the loaded activity: saves the pending edits first, sends nothing when that fails, and
   * replaces the shown sticker only on success. Does nothing while a generation runs, until {@link setPrepare} was
   * called, or until the current page's load landed. A failed generation keeps the shown sticker and is logged and
   * reported with an error snackbar, not thrown.
   *
   * @param teacherRequest Optional wish for the new sticker.
   * @param onRequestSent Runs once the pending edits saved and the request is about to be sent; never runs when they
   * did not save.
   */
  async generate(teacherRequest?: string, onRequestSent?: () => void): Promise<void> {
    const prepare = this.#prepare;
    const activityId = this.#loadedActivityId;

    if (this.#generating() || prepare === null || activityId === null) {
      return;
    }

    const abortController = new AbortController();
    const run: StickerGenerationRun = {
      number: ++this.#runNumber,
      activityId,
      prepare,
      signal: abortController.signal,
      teacherRequest,
      onRequestSent,
    };
    this.#abortController = abortController;
    this.#pickRegenerationActionLabels();
    this.#generating.set(true);

    try {
      await this.#runGeneration(run);
    } finally {
      if (this.#isCurrent(run.number)) {
        this.#abortController = null;
        this.#generating.set(false);
      }
    }
  }

  /**
   * Cancels the generation that is running, if any: a pending `prepare()` is left to finish but no request follows
   * it, and a request in flight is cancelled. Resets {@link generating}, shows no snackbar and keeps the shown
   * sticker. Does nothing when nothing is generating.
   */
  abort(): void {
    if (this.#generating()) {
      this.#invalidateRun();
    }
  }

  /** Forgets the shown sticker, the registered `prepare` and any generation still running. */
  reset(): void {
    this.#invalidateRun();
    this.#prepare = null;
    this.#stickerPath.set(null);
    this.#seenLoadCount = this.#editStore.draftLoadCount();
    this.#loadedActivityId = null;
  }

  startGenerationActions(): void {
    this.#stopGenerationActions();
    this.actualRegenerationActionIndex.set(0);

    for (let i = 0; i < this.#regenerationActionLabels().length; i++) {
      const timer = setTimeout(() => {
        this.actualRegenerationActionIndex.set(i);
      }, i * 2000);
      this.#actionTimers.push(timer);
    }
  }

  #pickRegenerationActionLabels(): void {
    const variant = REGENERATION_ACTION_VARIANTS[Math.floor(Math.random() * REGENERATION_ACTION_VARIANTS.length)];

    this.#regenerationActionLabels.set(
      REGENERATION_ACTION_STEPS.map((step) =>
        this.#transloco.translate(`${REGENERATION_ACTION_LABELS_KEY}.${variant}.${step}`),
      ),
    );
  }

  #stopGenerationActions(): void {
    for (const timer of this.#actionTimers) {
      clearTimeout(timer);
    }

    this.#actionTimers = [];
  }

  #seedWhenLoadLanded(loadCount: number): void {
    const draft = this.#editStore.draft();

    if (loadCount === this.#seenLoadCount || draft === null) {
      return;
    }

    this.#seenLoadCount = loadCount;

    if (draft.id !== this.#loadedActivityId) {
      this.#invalidateRun();
    }

    this.#loadedActivityId = draft.id;
    this.#stickerPath.set(draft.stickerImageUrl);
  }

  #followDraftSticker(path: string | null): void {
    const draft = this.#editStore.draft();

    if (draft === null) {
      return;
    }

    if (draft.id !== this.#loadedActivityId || this.#generating() || path === this.#stickerPath()) {
      return;
    }

    this.#stickerPath.set(path);
  }

  async #runGeneration(run: StickerGenerationRun): Promise<void> {
    const { number: runNumber, activityId } = run;
    const prepareResult = await this.#runPrepare(run.prepare);

    if (!this.#isCurrent(runNumber)) {
      return;
    }

    if (prepareResult !== 'ok') {
      if (prepareResult === 'failed') {
        this.#showSaveFailed();
      }

      return;
    }

    run.onRequestSent?.();
    this.startGenerationActions();

    try {
      const generation = await this.#activityService.generateSticker(activityId, {
        teacherRequest: run.teacherRequest,
        signal: run.signal,
      });

      if (this.#isCurrent(runNumber)) {
        this.#stickerPath.set(generation.stickerImageUrl);
      }
    } catch (error) {
      if (this.#isCurrent(runNumber)) {
        this.#logger.warn(`Activity sticker: generation failed for ${activityId}`, error);
        this.#showGenerationFailed(error);
      }
    } finally {
      if (this.#isCurrent(runNumber)) {
        this.#stopGenerationActions();
      }
    }
  }

  async #runPrepare(prepare: PrepareStickerGeneration): Promise<ActivityEditFlushResult> {
    try {
      const result = await prepare();

      return result === 'ok' && this.#editStore.saveUnavailable() ? 'failed' : result;
    } catch (error) {
      this.#logger.warn('Activity sticker: saving the pending edits failed', error);

      return 'failed';
    }
  }

  #invalidateRun(): void {
    this.#runNumber++;
    this.#abortController?.abort();
    this.#abortController = null;
    this.#stopGenerationActions();
    this.#generating.set(false);
  }

  #isCurrent(runNumber: number): boolean {
    return runNumber === this.#runNumber;
  }

  #showSaveFailed(): void {
    this.#showError(SAVE_FAILED_MESSAGE_KEY);
  }

  // Only the disabled image pipeline gets its own message; every other failure reads as "try again".
  #showGenerationFailed(error: unknown): void {
    const isPipelineDisabled =
      httpErrorStatus(error) === IMAGE_PIPELINE_DISABLED_STATUS &&
      httpErrorTitle(error) === IMAGE_PIPELINE_DISABLED_TITLE;

    this.#showError(isPipelineDisabled ? UNAVAILABLE_HERE_MESSAGE_KEY : GENERATION_FAILED_MESSAGE_KEY);
  }

  #showError(messageKey: string): void {
    this.#snackBar.open(
      {
        message: this.#transloco.translate(messageKey),
        testId: ACTIVITY_EDIT_STICKER_ERROR_SNACKBAR_TEST_ID,
      },
      { duration: 'long' },
    );
  }
}
